import { Injectable } from "@angular/core";
import { ActionSheetController } from "@ionic/angular";
import { Order } from "../models/order";
import { AccountService } from "./account.service";
import { from, map, switchMap, take } from "rxjs";
import { Vehicle, VehicleType } from "../models/vehicle";

@Injectable({
    providedIn: 'root'
})
export class NavigationService {
    private readonly WAZE_BASE_URL = 'https://waze.com/ul';
    private readonly GOOGLE_MAPS_BASE_URL = 'https://www.google.com/maps';

    constructor(
        private actionSheetController: ActionSheetController,
        private accountService: AccountService
    ) { }

    async startNavigation(order: Order) {
        this.accountService.getSelectedVehicle()
            .pipe(
                take(1),
                map(vehicle => {
                    if (!vehicle) {
                        return { type: VehicleType.Car };
                    }
                    return vehicle;
                }),
                switchMap(vehicle => {
                    return from(this.presentActionSheet(order.client.lat, order.client.lng, order.client.getAddress(), vehicle.type));
                })
            )
            .subscribe();
    }

    private async presentActionSheet(toLat: string, toLng: string, address: string, vehicleType: VehicleType) {
        let drivingMode = vehicleType == VehicleType.Car ? 'driving' : 'bicycling';
        const actionSheet = await this.actionSheetController.create({
            header: 'Navigate',
            buttons: [
                {
                    text: 'Cancel',
                    role: 'cancel'
                },
                {
                    text: 'Open Waze',
                    handler: () => {
                        const wazeUrl = this.constructWazeUrl(address, toLat, toLng);
                        window.open(wazeUrl);
                    }
                },
                {
                    text: 'Open Google Maps',
                    handler: () => {
                        const googleMapsUrl = this.constructGoogleMapsDirectionsUrl(toLat, toLng, drivingMode);
                        window.open(googleMapsUrl);
                    }
                }
            ]
        });

        await actionSheet.present();
    }

    private constructWazeUrl(address: string, lat: string, lng: string): string {
        if (!address || !lat || !lng) {
            throw new Error('Address, latitude, and longitude are required');
        }
        const params = new URLSearchParams({
            q: address,
            ll: `${lat},${lng}`,
            navigate: 'yes',
            z: '10'
        });
        return `${this.WAZE_BASE_URL}?${params.toString()}`;
    }

    private constructGoogleMapsDirectionsUrl(lat: string, lng: string, travelMode: string = 'driving'): string {
        if (!lat || !lng) {
            throw new Error('Latitude and longitude are required');
        }

        const params = new URLSearchParams({
            api: '1',
            destination: `${lat},${lng}`,
            travelmode: travelMode
        });
        return `${this.GOOGLE_MAPS_BASE_URL}/dir/?${params.toString()}`;
    }
}
